
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import Headers from "../Header";
import Footer from "../Footer";
import ScrollToTop from "../ScrollToTopButton/ScrollToTop"

const Features = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      title: "Comprehensive Question Banks",
      description: "Access thousands of high-quality questions across multiple domains. Create assessments in minutes by selecting from our curated question banks.",
      icon: "🧠",
      items: [
        "Pre-built question banks for technical roles",
        "Domain-specific assessments",
        "Multiple difficulty levels",
        "Regular updates with new questions"
      ]
    },
    {
      title: "Advanced Proctoring",
      description: "Ensure assessment integrity with our comprehensive proctoring features. Monitor candidates and prevent cheating.",
      icon: "🔒",
      items: [
        "Live webcam monitoring",
        "Screen recording",
        "Tab switching detection",
        "AI-powered behavior analysis"
      ]
    },
    {
      title: "Detailed Analytics",
      description: "Make data-driven decisions with comprehensive analytics and reporting. Understand candidate performance at a glance.",
      icon: "📊",
      items: [
        "Performance comparison",
        "Skill gap analysis",
        "Customizable reports",
        "Data export options"
      ]
    },
    {
      title: "Customizable Workflows",
      description: "Tailor the assessment process to your organization's needs. Create custom workflows for different roles and departments.",
      icon: "🎛️",
      items: [
        "Custom email templates",
        "Multi-stage assessments",
        "Automated scheduling",
        "Branded assessment experience"
      ]
    },
    {
      title: "Multi-level Access Control",
      description: "Define roles and permissions for team members. Ensure the right people have access to the right information.",
      icon: "👥",
      items: [
        "Role-based access control",
        "Custom permission sets",
        "Secure data access",
        "Activity logging and auditing"
      ]
    },
    {
      title: "Seamless Integration",
      description: "Connect AssessPro with your existing tools. Streamline your workflow with our integration capabilities.",
      icon: "🔄",
      items: [
        "API access",
        "ATS integration",
        "HRIS integration",
        "SSO authentication"
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      <Headers />
      {/* Navigation placeholder - would be replaced by your actual Navbar */}
      <div className="h-16 bg-white shadow"></div>
      
      {/* Hero section */}
      <section className="bg-gradient-to-b from-primary/10 to-white py-24">
        <div className="container mx-auto px-4 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            Powerful Features for Every Assessment Need
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-xl text-gray-700 max-w-3xl mx-auto mb-8"
          >
            Discover how AssessPro can transform your assessment process with our comprehensive suite of features.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Button 
              size="lg" 
              className="mr-4"
              asChild
            >
              <Link to="/register">Start Free Trial</Link>
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              asChild
            >
              <Link to="/demo">Request Demo</Link>
            </Button>
          </motion.div>
        </div>
      </section>
      
      {/* Features grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-xl shadow-md border hover:shadow-lg transition-shadow"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-2xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-6">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.items.map((item, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-primary py-16">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-6">Ready to Experience These Features?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join hundreds of companies that have transformed their assessment process with AssessPro.
          </p>
          <Button 
            variant="secondary" 
            size="lg"
            asChild
          >
            <Link to="/register">Start Your Free Trial Today</Link>
          </Button>
        </div>
      </section>
      
      {/* Footer placeholder - would be replaced by your actual Footer */}
      <div className="bg-gray-900 text-white py-16">
        <Footer />
      </div>
       <ScrollToTop />
    </div>
  );
};

export default Features;
